import { Button, useTheme } from '@material-ui/core'
import React from 'react'
import { useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { EventIcon, ArticleIcon, DiscussionIcon, } from "lib-core"

import { IconType } from 'react-icons'
import {
    MdGroup, MdPerson, MdBookmark,
    MdDrafts, MdNotifications, MdHome
} from 'react-icons/md'
import { TiPen } from 'react-icons/ti'
import { FaRegNewspaper } from 'react-icons/fa'
import { SiCircle as CircleIcon } from 'react-icons/si'
import { FiActivity } from 'react-icons/fi'
import { BiLayerPlus } from 'react-icons/bi'
import { CgAddR as CreatePageIcon } from 'react-icons/cg'

import { Dictionary, ReactFC } from '../types'

export const PrimaryPlaces = ['', 'notifications', 'create', 'groups', 'profile'] as const
export const ProfilePlaces = ['profile', 'profile/drafts', 'profile/bookmarks'] as const
export const HomePlaces = ['', 'events', 'activity', 'people'] as const
export const PlaceOverrides = [
    'profile/public/posts', 'profile/public', 'notifications/notifications',
    '/home', 'home/news', 'home/events', 'home/people', 'home/activity'

] as const
export type AllPlaces = typeof PrimaryPlaces[number] | typeof ProfilePlaces[number] | typeof PlaceOverrides[number] |
    typeof HomePlaces[number]


export interface NavigationButtonProps {
    place: AllPlaces
    level?: number
}

export { CreatePageIcon, EventIcon, ArticleIcon, DiscussionIcon }

export const navigationIcons: Dictionary<AllPlaces, IconType> = {
    '/home': MdHome,
    'create': BiLayerPlus,
    'home/activity': FiActivity,
    'home/news': FaRegNewspaper,
    'home/events': EventIcon,
    'home/people': MdGroup,
    'groups': CircleIcon,
    'profile/public': MdPerson,
    'profile/public/posts': TiPen,
    'profile/bookmarks': MdBookmark,
    'profile/drafts': MdDrafts,
    'notifications/notifications': MdNotifications,
}

export const usePlaceIcon = (place: AllPlaces, level = 0): IconType => {
    const id = placeId(place, level)
    const icon = navigationIcons[id]
    if (!icon) {
        return () => null
    }
    return icon
}

export const useLocationSelectedIcon = (href: string) => {
    const theme = useTheme()
    const selected = useLocationSelected(href)
    return {
        color: selected && theme.palette.primary.contrastText || undefined,
        size: theme.iconSize
    }
}


export const useLocationSelected = (href) => {
    const location = useLocation()
    if (!location.pathname.startsWith(href)) {
        return false
    }
    if (location.pathname === href) {
        return true
    }

    switch (href) {
        case "/":
            return HomePlaces.indexOf(location.pathname.slice(1) as any) !== -1
        case "/profile":
            return ProfilePlaces.indexOf(location.pathname.slice(1) as any) !== -1
    }
    return false
}


export const useNavigateHandler = (place: AllPlaces) => {
    const history = useHistory()
    const href = '/' + place
    return {
        onClick: useCallback(() => {
            history.push(href)
        }, [place, history]),
        href
    }
}

export const placeIdOverride: Record<number, Dictionary<AllPlaces, AllPlaces>> = {
    0: {
        'profile': 'profile/public',
        'notifications': 'notifications/notifications',
        '': '/home',
    },
    1: {
        'profile': 'profile/public/posts',
        'activity': 'home/activity',
        'events': 'home/events',
        '': 'home/news',
        'people': 'home/people',
    },
}

export const placeId = (place: AllPlaces, level = 0) => (placeIdOverride[level || 0] || {})[place] || place

export const navigationMessageId = (place: AllPlaces, level = 0) => {
    const id = placeId(place, level)
    return `navigation.${id}`
}

export const NavigationMessage: ReactFC<NavigationButtonProps> = ({ place, level }) => {
    return <FormattedMessage id={navigationMessageId(place, level)} />
}

export const NavigationButton: ReactFC<NavigationButtonProps> = ({ place }) => {
    const { href } = useNavigateHandler(place)
    return (
        <Button component={Link} to={href}>
            <NavigationMessage place={place} />
        </Button>
    )
}